import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, Loader2, Search, Archive, ArchiveRestore, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiFetch } from '@/lib/api';
import { getJson } from '@/lib/http';
import { usePackStatus } from '@/hooks/usePackStatus';
import type { Pack, Store, MediaFormat, Task } from '@shared/schema';

// Schema matching the API expectation
const packEditorSchema = z.object({
  name: z.string().min(1, 'Pack name is required').max(255, 'Pack name too long'),
  description: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  retailerId: z.number({ invalid_type_error: 'Retailer ID must be a number' }).int().positive('Retailer ID required'),
  stores: z.array(z.number({ invalid_type_error: 'Store ID must be a number' }).int().positive()).min(1, 'Select at least one store'),
  mediaFormats: z.array(z.object({
    mediaFormatId: z.number({ invalid_type_error: 'Media Format ID must be a number' }).int().min(1, 'Please select a media format'),
    quantity: z.number({ invalid_type_error: 'Quantity must be a number' }).int().min(1, 'Quantity must be at least 1')
  })).min(1, 'Select at least one media format'),
  taskId: z.number({ invalid_type_error: 'Task ID must be a number' }).int().positive().nullable().optional(),
});

type PackEditorFormData = z.infer<typeof packEditorSchema>;

interface PackEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit' | 'view';
  pack?: Pack | null;
  retailerId: number;
  onSuccess?: () => void;
}

interface PackDetailsResponse {
  pack: Pack;
  stores: Array<{ storeId: number }>;
  templates: Array<{ id: number; mediaFormatId: number; quantity: number }>;
  tasks: Array<{ id: number; taskId: number }>;
  integrity?: {
    hasIssues: boolean;
    invalidStoreCount: number;
    invalidStores: Array<{
      storeId: number;
      storeName: string;
      reason: 'deleted' | 'archived' | 'retailer_mismatch';
    }>;
    draftMismatchCount?: number;
    draftMismatchStores?: Array<{
      storeId: number;
      storeName: string;
    }>;
    hasDraftMismatch?: boolean;
    lastSavedStoreCount?: number;
    lastSavedVersion?: number | null;
  };
}

export function PackEditorDialog({
  isOpen,
  onClose,
  mode,
  pack,
  retailerId,
  onSuccess,
}: PackEditorDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = mode === 'edit' && !!pack;
  const isReadOnly = mode === 'view';
  const needsPackDetails = (mode === 'edit' || mode === 'view') && !!pack;
  const [detailsError, setDetailsError] = useState<string | null>(null);
  
  // Store search state
  const [storeSearch, setStoreSearch] = useState('');

  // Fetch pack details when editing or viewing
  const { data: packDetails, isLoading: isLoadingDetails, error: packDetailsError } = useQuery<PackDetailsResponse>({
    queryKey: ['/api/packs', pack?.id, 'details'],
    queryFn: async () => {
      if (!pack?.id) throw new Error('No pack ID');
      console.log('[PackEditor] Fetching details for pack:', pack.id);
      const response = await apiFetch(`/api/packs/${pack.id}/details`);
      if (!response.ok) {
        let errorMessage = `Failed to fetch pack details: ${response.status}`;
        try {
          const errorBody = await response.json();
          if (typeof errorBody?.message === 'string' && errorBody.message.trim()) {
            errorMessage = errorBody.message;
          } else if (typeof errorBody?.error === 'string' && errorBody.error.trim()) {
            errorMessage = errorBody.error;
          }
        } catch {
          // Ignore parse failure and keep the fallback status message.
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      console.log('[PackEditor] Loaded pack details:', data);
      return data;
    },
    enabled: isOpen && needsPackDetails && !!pack?.id,
    staleTime: 0, // Always fetch fresh
  });

  useEffect(() => {
    if (!isOpen) {
      setDetailsError(null);
      return;
    }
    if (needsPackDetails) {
      setDetailsError(null);
    }
  }, [isOpen, needsPackDetails, pack?.id]);

  useEffect(() => {
    // Mirror query errors into a local message so the dialog can render
    // a clear explanation instead of an empty form shell.
    if (!isLoadingDetails && needsPackDetails && !packDetails) {
      const nextMessage =
        packDetailsError instanceof Error && packDetailsError.message.trim()
          ? packDetailsError.message
          : 'Failed to load pack details.';
      setDetailsError(nextMessage);
    }
  }, [isLoadingDetails, needsPackDetails, packDetails, packDetailsError]);

  // Fetch stores for this retailer
  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['/api/retailers', retailerId, 'stores'],
    queryFn: async () => {
      const response = await apiFetch(`/api/retailers/${retailerId}/stores`);
      if (!response.ok) throw new Error('Failed to fetch stores');
      return response.json();
    },
    enabled: isOpen && !!retailerId,
  });

  // Fetch media formats for this retailer
  const { data: mediaFormats = [] } = useQuery<MediaFormat[]>({
    queryKey: ['/api/media-formats', retailerId],
    queryFn: async () => {
      const response = await apiFetch(`/api/media-formats?retailerId=${retailerId}`);
      if (!response.ok) throw new Error('Failed to fetch media formats');
      return response.json();
    },
    enabled: isOpen && !!retailerId,
  });

  // Fetch tasks
  const { data: tasks = [] } = useQuery<Task[]>({
    queryKey: ['/api/tasks'],
    queryFn: () => getJson('/api/tasks'),
    enabled: isOpen,
  });

  const defaultValues = useMemo((): PackEditorFormData => {
    if (needsPackDetails && packDetails) {
      console.log('[PackEditor] Computing defaultValues from packDetails:', {
        stores: packDetails.stores,
        templates: packDetails.templates,
        tasks: packDetails.tasks
      });
      return {
        name: packDetails.pack.name,
        description: packDetails.pack.description || '',
        isActive: packDetails.pack.isActive ?? true,
        retailerId: packDetails.pack.retailerId,
        stores: packDetails.stores.map(s => s.storeId),
        mediaFormats: packDetails.templates.map(t => ({
          mediaFormatId: t.mediaFormatId,
          quantity: t.quantity || 1,
        })),
        taskId: packDetails.tasks[0]?.taskId || null,
      };
    }

    console.log('[PackEditor] Computing defaultValues for create mode');
    return {
      name: '',
      description: '',
      isActive: true,
      retailerId,
      stores: [],
      mediaFormats: [{ mediaFormatId: 0, quantity: 1 }],
      taskId: 1, // Default to "Standard Posting (SuperValu)" - user can change it
    };
  }, [needsPackDetails, packDetails, retailerId]);

  const form = useForm<PackEditorFormData>({
    resolver: zodResolver(packEditorSchema),
    defaultValues,
    mode: 'onChange',
  });

  const { handleSubmit, control, watch, setValue, reset, formState: { errors, isSubmitting } } = form;

  // Reset form when dialog opens or when pack details load
  useEffect(() => {
    if (isOpen) {
      if (needsPackDetails) {
        // For edit/view mode, wait for pack details to load
        if (packDetails) {
          console.log('[PackEditor] Resetting form with loaded pack details');
          console.log('[PackEditor] Form values being set:', defaultValues);
          reset(defaultValues);
        } else {
          console.log('[PackEditor] Waiting for pack details to load...');
        }
      } else {
        // For create mode, reset immediately
        console.log('[PackEditor] Resetting form for create mode');
        reset(defaultValues);
      }
    }
  }, [isOpen, needsPackDetails, packDetails, defaultValues, reset]);

  const watchedMediaFormats = watch('mediaFormats');
  const watchedStores = watch('stores');
  const watchedTaskId = watch('taskId');

  // Filter stores based on search
  const filteredStores = useMemo(() => {
    if (!storeSearch.trim()) return stores;
    const searchLower = storeSearch.toLowerCase();
    return stores.filter(store => 
      store.name?.toLowerCase().includes(searchLower) ||
      store.address?.toLowerCase().includes(searchLower)
    );
  }, [stores, storeSearch]);

  // Reset search when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setStoreSearch('');
    }
  }, [isOpen]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: PackEditorFormData) => {
      if (!pack?.id) throw new Error('No pack ID for update');

      const payload = {
        pack: {
          name: data.name,
          description: data.description,
          retailerId: data.retailerId,
        },
        stores: data.stores,
        mediaFormats: data.mediaFormats.filter(f => f.mediaFormatId > 0),
        taskId: data.taskId,
      };

      console.log('[PackEditor] Updating pack with payload:', payload);

      // Update isActive separately via the archive endpoint
      if (pack.isActive !== data.isActive) {
        await apiFetch(`/api/packs/${pack.id}/archive`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isActive: data.isActive }),
        });
      }

      const response = await apiFetch(`/api/packs/${pack.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Update failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log('[PackEditor] Update successful:', result);
      return result;
    },
    onSuccess: () => {
      console.log('[PackEditor] Update succeeded, refreshing queries');
      queryClient.invalidateQueries({ queryKey: ['retailers', retailerId, 'packs'] });
      queryClient.invalidateQueries({ queryKey: ['/api/packs', pack?.id, 'details'] });
      toast({
        title: 'Pack updated successfully',
        description: `Pack "${form.getValues('name')}" has been updated.`,
      });
      onSuccess?.();
      onClose();
    },
    onError: (error: Error) => {
      console.error('[PackEditor] Update failed:', error);
      toast({
        title: 'Failed to update pack',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: PackEditorFormData) => {
      const payload = {
        pack: {
          name: data.name,
          description: data.description,
          retailerId: data.retailerId,
        },
        stores: data.stores,
        mediaFormats: data.mediaFormats.filter(f => f.mediaFormatId > 0),
        taskId: data.taskId,
      };

      console.log('[PackEditor] Creating pack with payload:', payload);

      const response = await apiFetch(`/api/packs/retailers/${retailerId}/packs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Create failed: ${response.status} ${errorText}`);
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retailers', retailerId, 'packs'] });
      toast({
        title: 'Pack created successfully',
        description: `Pack "${form.getValues('name')}" has been created.`,
      });
      onSuccess?.();
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create pack',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const onSubmit = async (data: PackEditorFormData) => {
    console.log('[PackEditor] Form submitted:', data);
    console.log('[PackEditor] Form errors:', errors);
    console.log('[PackEditor] isEditing:', isEditing);
    try {
      if (isEditing) {
        await updateMutation.mutateAsync(data);
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {
      console.error('[PackEditor] Submit error:', error);
    }
  };

  // Media format helpers
  const addMediaFormat = () => {
    const current = form.getValues('mediaFormats');
    setValue('mediaFormats', [...current, { mediaFormatId: 0, quantity: 1 }]);
  };

  const removeMediaFormat = (index: number) => {
    const current = form.getValues('mediaFormats');
    setValue('mediaFormats', current.filter((_, i) => i !== index));
  };

  const updateMediaFormat = (index: number, field: 'mediaFormatId' | 'quantity', value: number) => {
    const current = form.getValues('mediaFormats');
    const updated = [...current];
    updated[index] = { ...updated[index], [field]: value };
    setValue('mediaFormats', updated, { shouldDirty: true, shouldValidate: true });
  };

  // Store helpers
  const toggleStore = (storeId: number) => {
    const current = form.getValues('stores');
    const isSelected = current.includes(storeId);

    if (isSelected) {
      setValue('stores', current.filter(id => id !== storeId), { shouldDirty: true, shouldValidate: true });
    } else {
      setValue('stores', [...current, storeId], { shouldDirty: true, shouldValidate: true });
    }
  };

  const isLoading = updateMutation.isPending || createMutation.isPending || isSubmitting;
  const hasBlockingDraftMismatch = Boolean(packDetails?.integrity?.hasDraftMismatch);

  // Pack lock status
  const packIdForStatus = (mode !== 'create' && pack?.id) ? pack.id : 0;
  const { status: packLockStatus, isLoading: isLoadingLockStatus } = usePackStatus(packIdForStatus);
  // In edit mode the pack is always unlocked (PackEditButton enforces this).
  // In view mode, check the API. For create mode, not applicable.
  const isUnlocked = mode === 'edit' || packLockStatus === 'unlocked';
  const isArchived = pack?.isActive === false;

  // Archive/unarchive mutation
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const archiveMutation = useMutation({
    mutationFn: async (archive: boolean) => {
      if (!pack?.id) throw new Error('No pack ID');
      const res = await apiFetch(`/api/packs/${pack.id}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !archive }),
      });
      if (!res.ok) throw new Error(`Failed to ${archive ? 'archive' : 'unarchive'} pack`);
      return res.json();
    },
    onSuccess: (_data, archive) => {
      queryClient.invalidateQueries({ queryKey: ['retailers', retailerId, 'packs'] });
      queryClient.invalidateQueries({ queryKey: ['/api/packs', pack?.id, 'details'] });
      toast({
        title: archive ? 'Pack archived' : 'Pack unarchived',
        description: `"${pack?.name}" has been ${archive ? 'archived' : 'restored'}.`,
      });
      setConfirmingArchive(false);
      onSuccess?.();
      onClose();
    },
    onError: (error: Error, archive) => {
      toast({
        title: `Failed to ${archive ? 'archive' : 'unarchive'} pack`,
        description: error.message,
        variant: 'destructive',
      });
      setConfirmingArchive(false);
    },
  });

  // Reset confirmation state when dialog closes
  useEffect(() => {
    if (!isOpen) setConfirmingArchive(false);
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-white">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <DialogTitle>
              {mode === 'create' ? 'Create Pack Template' :
               mode === 'view' ? `View Pack: ${pack?.name || ''}` :
               `Edit Pack: ${pack?.name || ''}`}
            </DialogTitle>
            {mode === 'view' && pack && (
              <div className="flex items-center gap-2">
                {isArchived && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800">Archived</Badge>
                )}
                {!isLoadingLockStatus && packLockStatus && (
                  <Badge variant={packLockStatus === 'locked' ? 'default' : 'outline'}>
                    {packLockStatus === 'locked' ? 'Locked' : 'Unlocked'}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        {isLoadingDetails && needsPackDetails ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-3">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <div className="text-sm text-muted-foreground">Loading pack details...</div>
            </div>
          </div>
        ) : needsPackDetails && !packDetails ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {detailsError || 'Failed to load pack details.'}
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {packDetails?.integrity?.hasIssues && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-950">
                <AlertTriangle className="h-4 w-4 text-amber-700" />
                <AlertTitle>Stores removed since the last saved version</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    {packDetails.integrity.invalidStoreCount} store{packDetails.integrity.invalidStoreCount === 1 ? '' : 's'} in this pack
                    {' '}were deleted, archived, or moved. If you save or re-lock this pack, they will be removed from the pack.
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {packDetails.integrity.invalidStores.map((store) => (
                      <li key={`${store.storeId}-${store.reason}`}>
                        {store.storeName}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {packDetails?.integrity?.hasDraftMismatch && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Draft differs from the last saved version</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    This draft is missing {packDetails.integrity.draftMismatchCount} store{packDetails.integrity.draftMismatchCount === 1 ? '' : 's'}
                    {' '}that were present in the last saved version. Review the list below before saving or re-locking.
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {(packDetails.integrity.draftMismatchStores ?? []).map((store) => (
                      <li key={`draft-mismatch-${store.storeId}`}>
                        {store.storeName}
                      </li>
                    ))}
                  </ul>
                  <p>
                    Saving is disabled until the draft is restored, because continuing now could remove stores unexpectedly.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {/* Show validation errors */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800 font-semibold mb-1">Please fix the following errors:</p>
                <ul className="text-sm text-red-700 list-disc list-inside">
                  {Object.entries(errors).map(([field, error]) => (
                    <li key={field}>{error?.message?.toString() || `${field} is invalid`}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pack Name & Description */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Pack Information</h3>

              <div>
                <Label htmlFor="name">Pack Name *</Label>
                <Input
                  id="name"
                  {...form.register('name')}
                  placeholder="e.g., Metro Area Pack"
                  disabled={isReadOnly}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...form.register('description')}
                  placeholder="Brief description of the pack"
                  disabled={isReadOnly}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="isActive">Status *</Label>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value ? 'live' : 'archive'}
                      onValueChange={(value) => field.onChange(value === 'live')}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger id="isActive">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="archive">Archive</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.isActive && (
                  <p className="text-sm text-red-500 mt-1">{errors.isActive.message}</p>
                )}
              </div>

              {/* Attribution - View mode only */}
              {isReadOnly && packDetails?.pack && (
                <div>
                  <Label>Updated By</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {(packDetails.pack as any).lastUpdateReason === 'REASSIGNMENT' ? (
                      <>
                        {(packDetails.pack as any).lastUpdatedByName || 'Unknown'}{' '}
                        <span className="text-xs">
                          (Reassignment · {(packDetails.pack as any).lastUpdatedAt 
                            ? new Date((packDetails.pack as any).lastUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'})
                        </span>
                      </>
                    ) : (
                      <>
                        —{' '}
                        <span className="text-xs">
                          (Created · {(packDetails.pack as any).createdAt 
                            ? new Date((packDetails.pack as any).createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'})
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Media Formats */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Media Formats *</h3>
                {!isReadOnly && (
                  <Button type="button" variant="outline" size="sm" onClick={addMediaFormat}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Format
                  </Button>
                )}
              </div>

              {watchedMediaFormats.map((format, index) => (
                <div key={index} className="flex items-center space-x-2 p-3 border rounded-lg bg-gray-50">
                  <Select
                    value={format.mediaFormatId > 0 ? format.mediaFormatId.toString() : undefined}
                    onValueChange={(value) => {
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue)) {
                        updateMediaFormat(index, 'mediaFormatId', numValue);
                      }
                    }}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select media format" />
                    </SelectTrigger>
                    <SelectContent>
                      {mediaFormats.map((mf) => (
                        <SelectItem key={mf.id} value={mf.id.toString()}>
                          {mf.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex items-center space-x-2">
                    <Label className="text-sm">Qty:</Label>
                    <Input
                      type="number"
                      min="1"
                      value={format.quantity}
                      onChange={(e) => {
                        const numValue = parseInt(e.target.value, 10);
                        updateMediaFormat(index, 'quantity', isNaN(numValue) ? 1 : Math.max(1, numValue));
                      }}
                      className="w-20"
                      disabled={isReadOnly}
                    />
                  </div>

                  {!isReadOnly && watchedMediaFormats.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeMediaFormat(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}

              {errors.mediaFormats && (
                <p className="text-sm text-red-500">{errors.mediaFormats.message}</p>
              )}
            </div>

            {/* Stores */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Stores *</h3>

              {/* Store Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search stores..."
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setStoreSearch('');
                      e.currentTarget.blur();
                    }
                  }}
                  className="pl-9 pr-9"
                />
                {storeSearch && (
                  <button
                    type="button"
                    onClick={() => setStoreSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    title="Clear search (Esc)"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {stores.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No stores available
                  </div>
                ) : filteredStores.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No stores match "{storeSearch}"
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredStores.map((store) => (
                      <label
                        key={store.id}
                        className="flex items-center justify-between px-4 py-3 text-sm cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className="flex-1 pr-2">
                          <span className="font-medium">{store.name}</span>
                          {store.address && (
                            <span className="ml-2 text-muted-foreground text-xs">
                              {store.address}
                            </span>
                          )}
                        </span>
                        <input
                          type="checkbox"
                          checked={watchedStores.includes(store.id)}
                          onChange={() => toggleStore(store.id)}
                          className="h-4 w-4"
                          disabled={isReadOnly}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {errors.stores && (
                <p className="text-sm text-red-500">{errors.stores.message}</p>
              )}

              <div className="text-xs text-muted-foreground">
                Selected: {watchedStores.length} store{watchedStores.length === 1 ? '' : 's'}
                {storeSearch && ` · Showing ${filteredStores.length} of ${stores.length} stores`}
              </div>
            </div>

            {/* Task Binding */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Task Binding (Optional)</h3>
              <p className="text-sm text-muted-foreground">
                Bind a default task to this pack so campaigns inherit the appropriate workflow.
              </p>

              <Controller
                control={control}
                name="taskId"
                render={({ field }) => (
                  <Select
                    value={field.value ? String(field.value) : undefined}
                    onValueChange={(value) => {
                      if (value === '__none__') {
                        field.onChange(null);
                      } else {
                        const numValue = parseInt(value, 10);
                        field.onChange(isNaN(numValue) ? null : numValue);
                      }
                    }}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="No default task" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No default task</SelectItem>
                      {tasks.map((task) => (
                        <SelectItem key={task.id} value={task.id.toString()}>
                          {task.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Form Actions */}
            <div className="flex items-center pt-4 border-t">
              {/* Archive/Unarchive — unlocked packs only (edit mode = always unlocked, view mode = check API) */}
              {pack && mode !== 'create' && isUnlocked && (
                <div className="flex-1">
                  {!confirmingArchive ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={isArchived
                        ? 'text-green-700 border-green-300 hover:bg-green-50'
                        : 'text-amber-700 border-amber-300 hover:bg-amber-50'}
                      onClick={() => setConfirmingArchive(true)}
                    >
                      {isArchived ? (
                        <><ArchiveRestore className="h-4 w-4 mr-2" />Unarchive Pack</>
                      ) : (
                        <><Archive className="h-4 w-4 mr-2" />Archive Pack</>
                      )}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {isArchived ? 'Restore this pack?' : 'Archive this pack?'}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={archiveMutation.isPending}
                        onClick={() => archiveMutation.mutate(!isArchived)}
                      >
                        {archiveMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : null}
                        Confirm
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={archiveMutation.isPending}
                        onClick={() => setConfirmingArchive(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-2 ml-auto">
                <Button type="button" variant="outline" onClick={onClose}>
                  {isReadOnly ? 'Close' : 'Cancel'}
                </Button>

                {!isReadOnly && (
                  <Button 
                    type="submit" 
                    disabled={isSubmitting || updateMutation.isPending || createMutation.isPending || hasBlockingDraftMismatch}
                  >
                    {(isSubmitting || updateMutation.isPending || createMutation.isPending) ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {isEditing ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      isEditing ? 'Update Pack' : 'Create Pack'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
